<script setup lang="ts">
/**
 * "Find my sizes" — full-screen layer (Figma Quizzes › Measurements quiz · desktop
 * 3233:18690 / mobile 3234:19645). Sizes are matched from size charts, so nothing here
 * carries an AI mark (R209). Values are saved to Sizes as they are entered (R109); an
 * out-of-range value keeps the soft warning and stays (R113), the API refuses it today
 * (BE-145). × asks first; nothing is lost.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { MeasurementType } from '@/features/survey/types/survey-config'
import { useUpdateParameterMutation } from '@/mutations/parameters'
import { measurementCrossedRange, measurementRangeIssue } from '@/features/parameters/api/parameters'
import { useUpdatePersonalDataMutation } from '@/mutations/profile'
import { useAvailableBrandsQuery } from '@/queries/my-style'
import { useUserParametersQuery } from '@/queries/parameters'
import { useUserProfileDataQuery } from '@/queries/profile/useUserProfileDataQuery'
import { useAccountUnits } from '@/features/survey/model/useAccountUnits'
import { useSizeRecommendationBrandsQuery } from '@/queries/sizes'
import { PROFILE_SIZES_ROUTE } from '@/router/routes'
import { useFairCompact } from '@/composables/useFairCompact'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizSummary,
  FairQuizWelcome,
  settleFairQuizHistory,
  type FairQuizSummaryRow,
} from '@/ui/patterns/FairQuiz'
import { useFairToast } from '@/ui/patterns/FairToast'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairRemovableChip } from '@/ui/primitives/FairChip'
import { FairToggleItem } from '@/ui/primitives/FairToggleItem'
import {
  camelFairProfileV2Type,
  fairProfileV2Body,
  fairProfileV2MeasureFields,
  fairProfileV2UnitKind,
  fairProfileV2UnitLabel,
  fromFairProfileV2Display,
  isFairProfileV2OutOfRange,
  toFairProfileV2Display,
  type FairProfileV2Body,
  type FairProfileV2MeasureKey,
  type FairProfileV2Units,
} from '@/widgets/profile/fair-v2/fair-profile-v2-measure.data'
import {
  FAIR_PROFILE_V2_BODY_CATEGORIES,
  resolveFairProfileV2SizeRow,
} from '@/widgets/profile/fair-v2/fair-profile-v2-sizes.data'
import type { CategorySizeData, SurveyResults } from '@/types/sizes'
import { fetchSizesQuizResults } from '../api/sizes-quiz'
import { useSizesQuizStore } from '../store'
import {
  sizesQuizMoreBrands,
  sizesQuizProgress,
  sizesQuizRulerStep,
  sizesQuizStepId,
  sizesQuizSteps,
  type SizesQuizStep,
  type SizesQuizStepId,
} from '../sizes-quiz.data'
import SizesQuizField from './SizesQuizField.vue'
import './sizes-quiz.css'

const store = useSizesQuizStore()
const { t } = useI18n()
const router = useRouter()
const toast = useFairToast()
const compact = useFairCompact()

const profileQuery = useUserProfileDataQuery(computed(() => store.isOpen))
const parametersQuery = useUserParametersQuery()
const brandsQuery = useAvailableBrandsQuery()
const updateParameterMutation = useUpdateParameterMutation()
const personalDataMutation = useUpdatePersonalDataMutation()

/* ── Body and fields ──────────────────────────────────────────────────── */

/** One gender dictionary (`features/profile/gender.ts`), as the Sizes page. */
const bodyOf = fairProfileV2Body

const askGender = ref(false)
const body = ref<FairProfileV2Body | null>(null)
const withAdditional = ref(false)

const parameters = computed(() => parametersQuery.data.value?.parameters ?? [])
const parameterByType = computed(
  () => new Map(parameters.value.map((parameter) => [camelFairProfileV2Type(parameter.measurementType), parameter])),
)

const fields = computed(() =>
  body.value ? fairProfileV2MeasureFields(body.value, [...parameterByType.value.keys()]) : [],
)
const additionalCount = computed(() => fields.value.filter((field) => field.group === 'additional').length)

const steps = computed<SizesQuizStep[]>(() =>
  sizesQuizSteps({ askGender: askGender.value, fields: fields.value, withAdditional: withAdditional.value }),
)

/* ── Flow ─────────────────────────────────────────────────────────────── */

const stepId = ref<SizesQuizStepId>('welcome')
const step = computed<SizesQuizStep>(
  () => steps.value.find((item) => sizesQuizStepId(item) === stepId.value) ?? { kind: 'welcome' },
)
const fieldKey = computed<FairProfileV2MeasureKey | null>(() =>
  step.value.kind === 'field' ? step.value.key : null,
)
const field = computed(() => fields.value.find((item) => item.key === fieldKey.value) ?? null)

function goTo(id: SizesQuizStepId) {
  stepId.value = id
  store.save(id, withAdditional.value)
  if (id === 'done') void loadResults()
}

function move(by: 1 | -1) {
  const index = steps.value.findIndex((item) => sizesQuizStepId(item) === stepId.value)
  const next = steps.value[Math.max(0, Math.min(steps.value.length - 1, index + by))]
  if (next) goTo(sizesQuizStepId(next))
}

/* ── Values (cm / kg as stored) ───────────────────────────────────────── */

/** Entered in this session, by API type (an out-of-range one is saved soft, BE-145). */
const entered = ref<Record<string, number | null>>({})

function storedValue(type: string): number | null {
  return parameterByType.value.get(type)?.value ?? null
}

function currentValue(type: string): number | null {
  return type in entered.value ? entered.value[type] : storedValue(type)
}

/** BE-186 / BE-201 — the account's units (`profile-data.user.units`), written back on a switch. */
const accountUnits = useAccountUnits(computed(() => profileQuery.data.value?.user?.units))
const units = computed<FairProfileV2Units>(() => accountUnits.units.value)

const unitLabels = computed<Record<FairProfileV2Units, string>>(() => {
  const kind = fieldKey.value ? fairProfileV2UnitKind(fieldKey.value) : 'length'
  return { metric: fairProfileV2UnitLabel(kind, 'metric'), imperial: fairProfileV2UnitLabel(kind, 'imperial') }
})

const kind = computed(() => (fieldKey.value ? fairProfileV2UnitKind(fieldKey.value) : 'length'))
const displayValue = computed(() =>
  field.value ? toFairProfileV2Display(currentValue(field.value.type), kind.value, units.value) : null,
)
const range = computed(() => (field.value ? parameterByType.value.get(field.value.type)?.range ?? null : null))
const rulerStep = computed(() =>
  fieldKey.value ? sizesQuizRulerStep(fieldKey.value, units.value === 'imperial') : 1,
)
const rulerMin = computed(() => toFairProfileV2Display(range.value?.min ?? 0, kind.value, units.value) ?? 0)
const rulerMax = computed(() => toFairProfileV2Display(range.value?.max ?? 250, kind.value, units.value) ?? 250)

const warning = computed(() => {
  if (!field.value || !range.value) return ''
  const value = currentValue(field.value.type)
  if (!isFairProfileV2OutOfRange(value, range.value)) return ''
  return t('profile.sizes_page.out_of_range', {
    min: toFairProfileV2Display(range.value.min, kind.value, units.value),
    max: toFairProfileV2Display(range.value.max, kind.value, units.value),
    unit: fairProfileV2UnitLabel(kind.value, units.value),
  })
})

function setDisplayValue(value: number | null) {
  if (!field.value) return
  entered.value = {
    ...entered.value,
    [field.value.type]: fromFairProfileV2Display(value, kind.value, units.value),
  }
}

const saving = ref(false)

/** Saves the step's value if it changed and is in range; true when the step may move on. */
async function saveField(): Promise<boolean> {
  const current = field.value
  if (!current) return true
  const value = currentValue(current.type)
  if (value === null || value === storedValue(current.type)) return true
  // BE-145: outside the usual range the value is kept in soft mode (the warning stays).
  const soft = isFairProfileV2OutOfRange(value, range.value)
  saving.value = true
  try {
    await updateParameterMutation.mutateAsync({
      measurementType: current.type as MeasurementType,
      payload: soft ? { value, soft: true } : { value },
    })
    await parametersQuery.refresh(true)
    return true
  } catch (error) {
    console.error('Sizes quiz: measurement not saved', error)
    // BE-145: a hard-bound refusal names the range crossed instead of the generic error.
    const issue = measurementRangeIssue(error)
    const crossed = issue?.code === 'out_of_hard_range' ? measurementCrossedRange(issue) : null
    toast.show({
      tone: 'warning',
      message: crossed
        ? t('profile.sizes_page.out_of_hard_range', {
            min: toFairProfileV2Display(crossed.min, kind.value, units.value),
            max: toFairProfileV2Display(crossed.max, kind.value, units.value),
            unit: fairProfileV2UnitLabel(kind.value, units.value),
          })
        : t('sizes_quiz.save_error'),
    })
    return false
  } finally {
    saving.value = false
  }
}

async function continueField() {
  if (await saveField()) move(1)
}

/** Skip leaves the stored value as it was. */
function skip() {
  if (field.value) {
    const next = { ...entered.value }
    delete next[field.value.type]
    entered.value = next
  }
  move(1)
}

/* ── Gender (01, R120) ────────────────────────────────────────────────── */

const pickedGender = ref<'woman' | 'man' | null>(null)

async function continueGender() {
  const gender = pickedGender.value
  if (!gender) return
  saving.value = true
  try {
    await personalDataMutation.mutateAsync({ gender })
    body.value = gender === 'woman' ? 'feminine' : 'masculine'
    move(1)
  } catch (error) {
    console.error('Sizes quiz: gender not saved', error)
    toast.show({ tone: 'warning', message: t('sizes_quiz.save_error') })
  } finally {
    saving.value = false
  }
}

/* ── More measurements (10 / 11) ──────────────────────────────────────── */

const moreRows = computed<FairQuizSummaryRow[]>(() =>
  fields.value
    .filter((item) => item.group === 'additional')
    .map((item) => ({
      key: item.key,
      label: t(`profile.sizes_page.fields.${item.key}`),
      value: body.value ? t(`sizes_quiz.more.uses.${body.value}.${item.key}`) : '',
    })),
)

/** Brand chips need a logo (10 note); names that have none are left out. */
const servedBrandsQuery = useSizeRecommendationBrandsQuery(computed(() => store.isOpen))
const moreBrands = computed(() =>
  sizesQuizMoreBrands(servedBrandsQuery.data.value ?? [], brandsQuery.data.value ?? []),
)

function addMore() {
  withAdditional.value = true
  move(1)
}

/* ── Done (15) ────────────────────────────────────────────────────────── */

const results = ref<SurveyResults | null>(null)
const resultsLoading = ref(false)

async function loadResults() {
  resultsLoading.value = true
  try {
    results.value = await fetchSizesQuizResults()
  } catch (error) {
    console.error('Sizes quiz: sizes did not load', error)
    results.value = null
  } finally {
    resultsLoading.value = false
  }
}

const doneRows = computed<FairQuizSummaryRow[]>(() => {
  const current = body.value
  if (!current) return []
  const filled = new Set(parameters.value.filter((item) => item.value !== null).map((item) => camelFairProfileV2Type(item.measurementType)))
  return FAIR_PROFILE_V2_BODY_CATEGORIES[current].map((key) => {
    const data = (results.value?.[key] ?? null) as CategorySizeData | null
    return {
      key,
      label: t(`profile.sizes_page.categories.${current}.${key}.title`),
      value: resolveFairProfileV2SizeRow(key, data, filled).value ?? '',
    }
  })
})

async function openSizes() {
  store.close()
  await settleFairQuizHistory()
  await router.push(PROFILE_SIZES_ROUTE)
}

/* ── Header + leaving (19 / 20) ───────────────────────────────────────── */

const progress = computed(() => sizesQuizProgress(steps.value, stepId.value))
const headerRight = computed(() => (step.value.kind === 'field' ? 'skip' : 'none'))
const leaveOpen = ref(false)

function requestClose() {
  if (step.value.kind === 'welcome' || step.value.kind === 'done') {
    store.close()
    return
  }
  leaveOpen.value = true
}

async function exit() {
  leaveOpen.value = false
  // Autosave (R109): what is on the screen goes to Sizes before leaving.
  await saveField()
  store.close()
}

/* ── Open ─────────────────────────────────────────────────────────────── */

const ready = ref(false)

watch(
  () => store.session,
  async () => {
    ready.value = false
    entered.value = {}
    results.value = null
    pickedGender.value = null
    accountUnits.reset()
    const [, , saved] = await Promise.all([profileQuery.refresh(true), parametersQuery.refresh(true), store.load()])
    const known = bodyOf(profileQuery.data.value?.user?.gender)
    askGender.value = !known
    body.value = known
    withAdditional.value = Boolean(saved.withAdditional)
    ready.value = true
    const resume = saved.step && steps.value.some((item) => sizesQuizStepId(item) === saved.step)
    stepId.value = resume ? saved.step! : 'welcome'
    if (stepId.value === 'done') void loadResults()
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="sizes-quiz"
    :class="`sizes-quiz--${step.kind}`"
    :aria-label="t('sizes_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="skip"
    />

    <!-- 00 Welcome: the pill has no AI star (R209) -->
    <FairQuizWelcome
      v-if="step.kind === 'welcome'"
      :title="t('sizes_quiz.welcome.title')"
      :subtitle="t('sizes_quiz.welcome.subtitle')"
      :pills="[{ label: t('sizes_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      :continue-loading="!ready"
      @continue="move(1)"
    />

    <!-- 01 Gender -->
    <FairQuizStep
      v-else-if="step.kind === 'gender'"
      :title="t('sizes_quiz.gender.title')"
      :subtitle="t('sizes_quiz.gender.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="!pickedGender"
      :continue-loading="saving"
      @back="move(-1)"
      @continue="continueGender"
    >
      <div
        class="sizes-quiz__gender"
        role="radiogroup"
        :aria-label="t('sizes_quiz.gender.title')"
      >
        <FairToggleItem
          v-for="option in (['woman', 'man'] as const)"
          :key="option"
          :label="t(`sizes_quiz.gender.${option}`)"
          :selected="pickedGender === option"
          stretch
          large
          @click="pickedGender = option"
        />
      </div>
    </FairQuizStep>

    <!-- 02–09, 12–14, 16–19 Measurements -->
    <FairQuizStep
      v-else-if="step.kind === 'field' && field && body"
      fill
      :title="t(`sizes_quiz.fields.${field.key}.title`)"
      :subtitle="t(`sizes_quiz.fields.${field.key}.subtitle`)"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      :continue-disabled="displayValue === null"
      :continue-loading="saving"
      @back="move(-1)"
      @continue="continueField"
    >
      <SizesQuizField
        :key="field.key"
        :field-key="field.key"
        :body="body"
        :model-value="displayValue"
        :unit="fairProfileV2UnitLabel(kind, units)"
        :units="units"
        :unit-labels="unitLabels"
        :min="rulerMin"
        :max="rulerMax"
        :step="rulerStep"
        :warning="warning"
        :compact="compact"
        @update:model-value="setDisplayValue"
        @update:units="accountUnits.setUnits($event)"
      />
    </FairQuizStep>

    <!-- 10 / 11 More measurements -->
    <FairQuizStep
      v-else-if="step.kind === 'more'"
      :title="t('sizes_quiz.more.title')"
      :subtitle="t('sizes_quiz.more.subtitle')"
    >
      <div class="sizes-quiz-more">
        <section class="sizes-quiz-more__group">
          <h3 class="sizes-quiz-more__label">
            {{ t('sizes_quiz.more.what_they_add') }}
          </h3>
          <FairQuizSummary :rows="moreRows" />
        </section>
        <section
          v-if="moreBrands.length"
          class="sizes-quiz-more__group"
        >
          <h3 class="sizes-quiz-more__label">
            {{ t('sizes_quiz.more.brands_like') }}
          </h3>
          <div class="sizes-quiz-more__brands">
            <FairRemovableChip
              v-for="brand in moreBrands"
              :key="brand.name"
              :label="brand.name"
              :image-src="brand.logo"
              :removable="false"
              :size="compact ? 'mobile' : 'default'"
            />
          </div>
        </section>
      </div>
      <template #footer>
        <FairButton
          class="sizes-quiz-more__see"
          variant="tonal"
          size="auto"
          :label="t('sizes_quiz.more.see_sizes')"
          @click="goTo('done')"
        />
        <FairButton
          class="fair-quiz-card__continue"
          variant="primary"
          size="auto"
          :label="t('sizes_quiz.more.add', { n: additionalCount })"
          @click="addMore"
        />
      </template>
    </FairQuizStep>

    <!-- 15 Done: no AI mark, no lavender (R209) -->
    <FairQuizStep
      v-else-if="step.kind === 'done'"
      class="sizes-quiz__done"
      :title="t('sizes_quiz.done.title')"
      :subtitle="t('sizes_quiz.done.subtitle')"
      :show-back="false"
      :continue-label="t('sizes_quiz.done.see_sizes')"
      :continue-loading="resultsLoading"
      @continue="openSizes"
    >
      <FairQuizSummary
        :rows="doneRows"
        :empty="t('sizes_quiz.done.empty')"
      />
    </FairQuizStep>

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('sizes_quiz.leave.title')"
      :text="t('sizes_quiz.leave.text')"
      :keep-label="t('sizes_quiz.leave.keep')"
      :exit-label="t('sizes_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
